import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Download, RefreshCw, Share2, Link2, Check, ExternalLink } from 'lucide-react';
import { FaInstagram } from 'react-icons/fa';
import { DashboardLayout } from '@/components/layout';
import { Card, Button, Badge } from '@/components/ui';
import { useToast } from '@/components/ui';
import { useInstagram } from '@/hooks/useInstagram';
import { useAuth } from '@/hooks/useAuth';
import { createContentLink, getContentLinkByContentId } from '@/services/contentLinks';
import type { PlatformContent, ContentLink } from '@/types';

const Contents = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToast } = useToast();
  const { user } = useAuth();
  const { connection, contents, loading, syncing, connect, sync } = useInstagram();

  // Track content links per content id
  const [contentLinks, setContentLinks] = useState<Record<string, ContentLink>>({});
  const [creatingLink, setCreatingLink] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  // Handle Instagram OAuth callback
  useEffect(() => {
    const igStatus = searchParams.get('instagram');
    if (igStatus === 'connected') {
      addToast({ type: 'success', title: t('settings.instagramConnectionSuccess') });
      setSearchParams({}, { replace: true });
    } else if (igStatus === 'error') {
      const reason = searchParams.get('reason') || 'unknown';
      const reasonKey = `settings.instagramError_${reason}`;
      const message = i18n.exists(reasonKey) ? t(reasonKey) : t('settings.instagramError_unknown');
      addToast({ type: 'error', title: t('settings.instagramConnectionError'), message });
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, addToast, t, i18n]);

  const handleConnect = async () => {
    try {
      await connect();
    } catch {
      addToast({ type: 'error', title: t('settings.instagramConnectionError') });
    }
  };

  const handleSync = async () => {
    try {
      const result = await sync();
      if (result) {
        addToast({
          type: 'success',
          title: t('settings.instagramSyncSuccess'),
          message: t('settings.instagramSyncResult', { count: result.totalFetched }),
        });
      }
    } catch {
      addToast({ type: 'error', title: t('settings.instagramSyncError') });
    }
  };

  const handleShareContent = useCallback(async (content: PlatformContent) => {
    if (!user || creatingLink) return;

    setCreatingLink(content.id);
    try {
      // Check if a link already exists
      let link = contentLinks[content.id];
      if (!link) {
        link = await getContentLinkByContentId(user.uid, content.id) ?? undefined as unknown as ContentLink;
      }

      // Create one if it doesn't exist
      if (!link) {
        const title = content.caption?.substring(0, 60) || '';
        link = await createContentLink(user.uid, content.id, title);
      }

      // Store it
      setContentLinks((prev) => ({ ...prev, [content.id]: link }));

      // Copy to clipboard
      const url = `https://klipr.it/${link.code}`;
      await navigator.clipboard.writeText(url);
      setCopiedCode(content.id);
      addToast({
        type: 'success',
        title: t('contents.linkCopied'),
        message: url,
      });

      setTimeout(() => setCopiedCode(null), 2000);
    } catch {
      addToast({ type: 'error', title: t('contents.linkError') });
    } finally {
      setCreatingLink(null);
    }
  }, [user, contentLinks, creatingLink, addToast, t]);

  return (
    <DashboardLayout>
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-dark-800 dark:text-white mb-2">{t('contents.title')}</h1>
        <p className="text-gray-500 dark:text-gray-400">{t('contents.subtitle')}</p>
      </div>

      {/* Connection status */}
      {!connection || connection.status !== 'active' ? (
        <Card variant="default" padding="lg" className="max-w-lg">
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <FaInstagram className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-xl font-semibold text-dark-800 dark:text-white mb-2">
              {t('contents.connectTitle')}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              {t('contents.connectDescription')}
            </p>
            <Button variant="primary" onClick={handleConnect} isLoading={loading}>
              <FaInstagram className="w-4 h-4 mr-2" />
              {t('settings.connectInstagram')}
            </Button>
          </div>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Header with sync button */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {connection.metadata?.profilePictureUrl ? (
                <img
                  src={String(connection.metadata.profilePictureUrl)}
                  alt={connection.platformUsername}
                  className="w-10 h-10 rounded-full"
                />
              ) : null}
              <div>
                <p className="font-medium text-dark-800 dark:text-white">@{connection.platformUsername}</p>
                <Badge variant="lime" size="sm">{t('settings.instagramConnected')}</Badge>
              </div>
            </div>
            <Button variant="secondary" onClick={handleSync} isLoading={syncing}>
              <RefreshCw className="w-4 h-4 mr-2" />
              {syncing ? t('settings.instagramSyncing') : t('settings.instagramSyncContent')}
            </Button>
          </div>

          {/* Content grid */}
          {contents.length === 0 ? (
            <Card variant="default" padding="lg">
              <div className="text-center py-12">
                <Download className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400 mb-4">{t('contents.empty')}</p>
                <Button variant="primary" onClick={handleSync} isLoading={syncing}>
                  {t('settings.instagramSyncContent')}
                </Button>
              </div>
            </Card>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {contents.map((content) => {
                const hasLink = !!contentLinks[content.id];
                const isCopied = copiedCode === content.id;
                const isCreating = creatingLink === content.id;

                return (
                  <Card key={content.id} variant="default" padding="none" className="overflow-hidden group">
                    <div className="aspect-square bg-gray-100 dark:bg-dark-700 relative">
                      {content.thumbnailUrl || content.storageUrl ? (
                        <img
                          src={content.thumbnailUrl || content.storageUrl || ''}
                          alt={content.caption || ''}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Download className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                        </div>
                      )}
                      {/* Media type badge */}
                      <div className="absolute top-2 right-2">
                        <Badge variant={content.mediaType === 'VIDEO' ? 'lavender' : 'lime'} size="sm">
                          {content.mediaProductType || content.mediaType}
                        </Badge>
                      </div>
                      {/* Status indicator */}
                      {content.status === 'pending' && (
                        <div className="absolute bottom-2 left-2">
                          <Badge variant="lavender" size="sm">{t('contents.pending')}</Badge>
                        </div>
                      )}
                      {content.status === 'failed' && (
                        <div className="absolute bottom-2 left-2">
                          <Badge variant="lavender" size="sm" className="!bg-red-100 !text-red-700">{t('contents.failed')}</Badge>
                        </div>
                      )}
                      {/* Hover overlay with share button */}
                      {content.status === 'stored' && (
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <button
                            onClick={() => handleShareContent(content)}
                            disabled={isCreating}
                            className="bg-white/90 hover:bg-white text-dark-800 rounded-full p-3 transition-all transform hover:scale-105 disabled:opacity-50"
                          >
                            {isCreating ? (
                              <RefreshCw className="w-5 h-5 animate-spin" />
                            ) : isCopied ? (
                              <Check className="w-5 h-5 text-green-600" />
                            ) : (
                              <Share2 className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      {content.caption && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">{content.caption}</p>
                      )}
                      {/* Share link row */}
                      {hasLink && (
                        <div className="flex items-center gap-2 mt-1">
                          <Link2 className="w-3.5 h-3.5 text-lime-500 flex-shrink-0" />
                          <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            klipr.it/{contentLinks[content.id].code}
                          </span>
                          <a
                            href={`https://klipr.it/${contentLinks[content.id].code}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-shrink-0 text-gray-400 hover:text-lime-500 transition-colors"
                          >
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}
    </DashboardLayout>
  );
};

export default Contents;
